import type { AuthTokens } from '@/entities/user';

import { setPersistedUserSnapshot } from './userSnapshot';

/**
 * Single source of truth for token persistence.
 *
 * Tokens live in `localStorage` (see TECHNICAL_DESIGN.md §3.8). Everywhere
 * else in the codebase reads / writes through these helpers so we can:
 *   - change the storage strategy in one place (e.g. to `sessionStorage`);
 *   - keep the exact string keys in one audit-able location;
 *   - fire the multi-tab sync event on every write without boilerplate.
 */

export const ACCESS_TOKEN_KEY = 'byq.access';
export const REFRESH_TOKEN_KEY = 'byq.refresh';

type Subscriber = (access: string | null) => void;

const subscribers = new Set<Subscriber>();

/** Guarded storage accessor — works in SSR / test-node contexts. */
function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  return storage()?.getItem(ACCESS_TOKEN_KEY) ?? null;
}

export function getRefreshToken(): string | null {
  return storage()?.getItem(REFRESH_TOKEN_KEY) ?? null;
}

export function setTokens({ accessToken, refreshToken }: AuthTokens): void {
  const s = storage();
  if (!s) return;
  s.setItem(ACCESS_TOKEN_KEY, accessToken);
  s.setItem(REFRESH_TOKEN_KEY, refreshToken);
  notify(accessToken);
}

export function clearTokens(): void {
  const s = storage();
  if (!s) return;
  s.removeItem(ACCESS_TOKEN_KEY);
  s.removeItem(REFRESH_TOKEN_KEY);
  setPersistedUserSnapshot(null);
  notify(null);
}

export function hasTokens(): boolean {
  return Boolean(getAccessToken() && getRefreshToken());
}

/**
 * Subscribe to *cross-tab* changes to the access token. The `storage` event
 * does not fire in the tab that made the change, so same-tab writes notify
 * directly via our internal subscriber set.
 */
export function subscribeToTokenChanges(handler: Subscriber): () => void {
  subscribers.add(handler);

  const onStorage = (event: StorageEvent) => {
    if (event.key !== ACCESS_TOKEN_KEY) return;
    handler(event.newValue);
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('storage', onStorage);
  }

  return () => {
    subscribers.delete(handler);
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', onStorage);
    }
  };
}

function notify(access: string | null) {
  for (const sub of subscribers) sub(access);
}
